<template>
  <div class="phone-number-input d-flex gap-2">
    <div class="dial-code-select flex-shrink-0">
      <SearchableSelectComponent
        :options="dialCodeOptions"
        :modelValue="selectedCountry"
        :placeholder="'+—'"
        :invalid="invalid"
        @update:modelValue="onCountrySelected"
      />
    </div>
    <input
      :id="id"
      type="tel"
      class="form-control flex-grow-1"
      :class="{ 'is-invalid': invalid }"
      :value="localNumber"
      :placeholder="placeholder"
      @input="onNumberInput"
      @blur="$emit('blur')"
    />
  </div>
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { COUNTRY_NAMES, getFlagEmoji } from "@/utils/countries";
import { getDialCode, getDialCodeCountries } from "@/utils/countryDialCodes";
import { computed, ref, watch } from "vue";

// ISO alpha-2 -> dial code, sourced once from libphonenumber-js, for building the picker and
// matching a leading "+<dialcode>" back to a country.
const DIAL_CODES: Array<[string, string]> = getDialCodeCountries().map(
  (iso) => [iso, getDialCode(iso) as string]
);

const props = withDefaults(
  defineProps<{
    /** The full phone number string, e.g. "+372 5123456". This is what gets submitted. */
    modelValue: string | null | undefined;
    /** ISO alpha-2 code of the company/signup country; seeds the dial-code default. */
    countryCode?: string;
    invalid?: boolean;
    id?: string;
    placeholder?: string;
  }>(),
  { countryCode: "", invalid: false, id: undefined, placeholder: "" }
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [];
}>();

// Countries sorted by name, each shown as "🇪🇪 +372" with the country name as searchable
// description so the collapsed control stays compact but typing a country name still finds it.
const dialCodeOptions = computed(() =>
  DIAL_CODES.map(([iso, dial]) => ({
    value: iso,
    label: `${getFlagEmoji(iso)} +${dial}`,
    description: COUNTRY_NAMES[iso] ?? iso,
  })).sort((a, b) => a.description.localeCompare(b.description))
);

const selectedCountry = ref<string>("");
const localNumber = ref<string>("");
// Once the admin/user deliberately picks a dial code we stop auto-syncing it to the company
// country, so changing the country afterwards doesn't silently overwrite their choice.
const dialCodeManuallySet = ref(false);

const combined = computed(() => {
  const number = localNumber.value.trim();
  if (!number) return "";
  const dial = getDialCode(selectedCountry.value);
  return dial ? `+${dial} ${number}` : number;
});

/** Longest-prefix match of a leading "+<dialcode>" against the known dial codes. */
const matchDialPrefix = (raw: string): { iso: string; dial: string } | null => {
  const digits = raw.replace(/[^\d]/g, "");
  const candidates = DIAL_CODES.filter(([, dial]) =>
    digits.startsWith(dial)
  ).sort((a, b) => b[1].length - a[1].length);
  if (candidates.length === 0) return null;
  const dial = candidates[0][1];
  // Prefer the country matching the supplied countryCode when several share the dial code (e.g. +1).
  const preferred = candidates.find(
    ([iso]) => iso === (props.countryCode || "").toUpperCase()
  );
  return { iso: preferred?.[0] ?? candidates[0][0], dial };
};

const defaultCountryFromProp = () => {
  const iso = (props.countryCode || "").toUpperCase();
  return getDialCode(iso) ? iso : "";
};

const parseValue = (value: string | null | undefined) => {
  const raw = (value ?? "").trim();
  // The company/signup country is the requested default and wins over any prefix already on the
  // value (e.g. a stale profile number from another country); we only fall back to the value's
  // own prefix when no company country is known.
  const preferred = defaultCountryFromProp();

  if (!raw) {
    localNumber.value = "";
    if (!dialCodeManuallySet.value) selectedCountry.value = preferred;
    return;
  }
  if (raw.startsWith("+")) {
    const match = matchDialPrefix(raw);
    if (match) {
      // Drop exactly the "+<dial>" prefix (using the matched dial length, not a greedy \d+ that
      // would swallow the local digits when there's no separator, e.g. "+37255512345") and any
      // following separator, to leave just the local part.
      localNumber.value = raw
        .slice(1 + match.dial.length)
        .replace(/^[\s-]+/, "")
        .trim();
      if (!dialCodeManuallySet.value) {
        selectedCountry.value = preferred || match.iso;
      }
      return;
    }
  }
  localNumber.value = raw;
  if (!dialCodeManuallySet.value) {
    selectedCountry.value = preferred || selectedCountry.value;
  }
};

const onCountrySelected = (iso: string | undefined) => {
  selectedCountry.value = iso ?? "";
  dialCodeManuallySet.value = true;
  emit("update:modelValue", combined.value);
};

const onNumberInput = (event: Event) => {
  localNumber.value = (event.target as HTMLInputElement).value;
  emit("update:modelValue", combined.value);
};

// Re-parse only when the parent sets a value that isn't the one we just produced, so the
// emit -> prop round-trip doesn't fight our internal state.
watch(
  () => props.modelValue,
  (value) => {
    if ((value ?? "") === combined.value) return;
    parseValue(value);
    // When the company-country default re-attributes a prefilled number (e.g. a stale +34 profile
    // number in a PL signup), push the reconciled value back so the submitted value matches what
    // the field now shows instead of silently keeping the old country code.
    if (combined.value !== (value ?? ""))
      emit("update:modelValue", combined.value);
  },
  { immediate: true }
);

// Follow the company country until the user overrides the dial code themselves.
watch(
  () => props.countryCode,
  () => {
    if (dialCodeManuallySet.value) return;
    const iso = defaultCountryFromProp();
    if (iso && iso !== selectedCountry.value) {
      selectedCountry.value = iso;
      if (localNumber.value.trim()) emit("update:modelValue", combined.value);
    }
  }
);
</script>

<style scoped lang="scss">
.dial-code-select {
  width: 9rem;
}
</style>
